import isPropValid from '@emotion/is-prop-valid'
import { css, type SerializedStyles } from '@emotion/react'
import styled from '@emotion/styled'
import { createLink } from '@tanstack/react-router'
import { forwardRef, type MouseEvent, memo, useCallback, useContext, useMemo } from 'react'

import { hoverCapableMediaQuery } from '../../code-only/hooks/use-has-hover.ts'
import { useUiContext } from '../../code-only/hooks/use-screen-variant.ts'
import { borderRadius } from '../../foundations/border-radius-tokens.ts'
import { colorTokens } from '../../foundations/color-tokens.ts'
import { shadow } from '../../foundations/shadow-tokens.ts'
import { spacing } from '../../foundations/spacing-tokens.ts'
import { Text } from '../../foundations/text/index.ts'
import { TrackButtonClickContext } from '../../wrapper-context.tsx'
import { Avatar } from '../avatar/avatar.tsx'
import { AvatarContext } from '../avatar/context.ts'
import { Spinner } from '../spinner/index.ts'
import { Tooltip } from '../tooltip/tooltip.tsx'

import { ButtonContext } from './context.ts'
import type { ButtonProps, ButtonStyle, ButtonVariant } from './props.ts'
import { buttonDimensionsPerButtonSize } from './sizes.ts'

type ButtonTheme = {
  [style in ButtonStyle]: {
    [state in ButtonState]: { backgroundColor: string; color: string; iconColor: string; borderColor?: string }
  }
}

export const Button = memo(
  forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
    const {
      variant,
      size,
      disabled,
      textVariant = 'button',
      style = 'base',
      fullWidth = false,
      avatar,
      icon,
      avatarPosition = 'right',
      type = 'button',
      onClick,
      children,
      className,
      ariaLabel,
      title,
      isLoading,
      trackingId,
      form,
      dataTestId,
      screenVariant,
      avatarSize,
      arrowHorizontalPosition,
      arrowVerticalPosition,
      ...linkProps
    } = props

    const contextValue = useContext(ButtonContext)
    const resolvedDisabled = contextValue.disabled ?? disabled ?? false
    const resolvedVariant = contextValue.variant ?? variant ?? 'primary'
    const resolvedSize = contextValue.size ?? size ?? 'medium'

    const avatarContext = useMemo(() => ({ size: avatarSize ?? '30' }) as const, [avatarSize])

    const localScreenVariant = useUiContext()

    const iconOnly = !children
    const setupAvatar = useMemo(
      () =>
        avatar || icon ? (
          <AvatarContext.Provider value={avatarContext}>
            <IconWrapper>{avatar || <Avatar icon={icon} />}</IconWrapper>
          </AvatarContext.Provider>
        ) : null,
      [avatar, avatarContext, icon],
    )

    const trackButtonContext = useContext(TrackButtonClickContext)

    const handleClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        trackButtonContext({
          buttonName: trackingId,
          buttonText: event.currentTarget.textContent || undefined,
          buttonIcon: icon,
        })
        onClick?.(event)
      },
      [icon, onClick, trackButtonContext, trackingId],
    )

    const button = (
      <StyledButton
        ref={ref}
        as={!resolvedDisabled && 'href' in props ? 'a' : 'button'}
        size={resolvedSize}
        variant={resolvedVariant}
        buttonStyle={style}
        fullWidth={fullWidth}
        avatarPosition={avatarPosition}
        iconOnly={iconOnly}
        isLoading={isLoading}
        disabled={resolvedDisabled}
        type={isLoading ? 'button' : type}
        className={className}
        onClick={isLoading || ('href' in props && 'onClick' in props) ? onClick : handleClick}
        aria-label={ariaLabel}
        form={form}
        data-testid={dataTestId}
        {...linkProps}
      >
        {isLoading ? (
          <div
            aria-hidden
            css={css`
              position: absolute;
              inset: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              pointer-events: none;
            `}
          >
            <Spinner strokeColor={getSpinnerColor(resolvedDisabled, resolvedVariant, style)} size="30" />
          </div>
        ) : undefined}
        {children ? (
          <div
            css={css`
              flex-direction: column;
              display: flex;
              align-items: center;
              position: relative;
              max-width: 100%;
            `}
          >
            <Text
              screenVariant={screenVariant ?? localScreenVariant}
              variant={textVariant}
              css={css`
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                visibility: ${isLoading ? 'hidden' : 'visible'};
                max-width: 100%;
              `}
            >
              {children}
            </Text>
          </div>
        ) : null}
        {setupAvatar &&
          (isLoading ? (
            <span
              aria-hidden
              css={css`
                visibility: hidden;
              `}
            >
              {setupAvatar}
            </span>
          ) : (
            setupAvatar
          ))}
      </StyledButton>
    )

    if (!title) return button

    return (
      <Tooltip
        title={title}
        arrowHorizontalPosition={arrowHorizontalPosition}
        arrowVerticalPosition={arrowVerticalPosition}
      >
        {button}
      </Tooltip>
    )
  }),
)

export const ButtonLink = createLink(Button)

const prosThatShouldNotBeForwarded = new Set([
  'buttonStyle',
  'iconOnly',
  'size',
  'variant',
  'theme',
  'style',
  'fullWidth',
  'avatar',
  'icon',
  'avatarPosition',
  'title',
  'isLoading',
  'trackingId',
])

const buttonShouldForwardProp = (propName: string) =>
  !prosThatShouldNotBeForwarded.has(propName) && isPropValid(propName)

type NoUndefinedField<T> = { [P in keyof T]-?: NoUndefinedField<NonNullable<T[P]>> }
type StyledButtonProps = NoUndefinedField<Pick<ButtonProps, 'size' | 'variant' | 'fullWidth' | 'avatarPosition'>> & {
  buttonStyle: ButtonStyle
  iconOnly: boolean
  isLoading?: boolean | undefined
  to?: string | undefined
  target?: string | undefined
}

const StyledButton = styled('button', {
  shouldForwardProp: buttonShouldForwardProp,
})<StyledButtonProps>`
  flex-shrink: 0;
  outline: none;
  position: ${({ isLoading }) => (isLoading ? 'relative' : undefined)};

  display: ${({ fullWidth }) => (fullWidth ? 'flex' : 'inline-flex')};
  flex-direction: ${({ avatarPosition }) => (avatarPosition === 'left' ? 'row-reverse' : 'row')};
  text-decoration: none;
  justify-content: center;
  align-items: center;

  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};

  border: none;
  cursor: pointer;

  transition: all 0.25s ease-in-out;

  &:disabled {
    pointer-events: none;
    box-shadow: none !important;
  }

  ${({ variant }) => buttonStylePerVariant[variant]}
`

const sizedStyle = (props: StyledButtonProps) => css`
  gap: ${spacing[30]};

  height: ${buttonDimensionsPerButtonSize[props.size].height};
  ${
    props.iconOnly
      ? `width: ${buttonDimensionsPerButtonSize[props.size].height}; padding: ${buttonDimensionsPerButtonSize[props.size].iconPadding};`
      : `padding: ${buttonDimensionsPerButtonSize[props.size].padding};`
  };

  border-radius: ${borderRadius[20]};
`

const primaryStyle = (props: StyledButtonProps) => css`
  ${sizedStyle(props)}

  box-shadow: ${shadow.bottom['05']};

  background-color: ${primaryColorsPerButtonStylePerTheme[props.buttonStyle].idle.backgroundColor};
  color: ${primaryColorsPerButtonStylePerTheme[props.buttonStyle].idle.color};

  --icon-color: ${primaryColorsPerButtonStylePerTheme[props.buttonStyle].idle.iconColor};

  ${hoverCapableMediaQuery} {
    &:hover {
      background-color: ${primaryColorsPerButtonStylePerTheme[props.buttonStyle].hover.backgroundColor};
      color: ${primaryColorsPerButtonStylePerTheme[props.buttonStyle].hover.color};
      box-shadow: ${props.buttonStyle !== 'decorative' ? shadow.bottom['05'] : 0};

      --icon-color: ${primaryColorsPerButtonStylePerTheme[props.buttonStyle].hover.iconColor};
    }
  }

  &:active,
  &:focus-visible {
    background-color: ${primaryColorsPerButtonStylePerTheme[props.buttonStyle].active.backgroundColor};
    color: ${primaryColorsPerButtonStylePerTheme[props.buttonStyle].active.color};
    box-shadow: ${props.buttonStyle !== 'decorative' ? shadow.bottom['05'] : 0};

    --icon-color: ${primaryColorsPerButtonStylePerTheme[props.buttonStyle].active.iconColor};
  }

  &:disabled {
    background-color: ${primaryColorsPerButtonStylePerTheme[props.buttonStyle].disabled.backgroundColor};
    color: ${primaryColorsPerButtonStylePerTheme[props.buttonStyle].disabled.color};
    box-shadow: ${props.buttonStyle !== 'decorative' ? shadow.bottom['05'] : 0};

    --icon-color: ${primaryColorsPerButtonStylePerTheme[props.buttonStyle].disabled.iconColor};
  }
`

type ButtonState = 'idle' | 'hover' | 'active' | 'disabled'

const secondaryStyle = (props: StyledButtonProps) => css`
  ${sizedStyle(props)}

  box-shadow: ${shadow.bottom['05']};

  background-color: ${secondaryColorsPerButtonStylePerTheme[props.buttonStyle].idle.backgroundColor};
  color: ${secondaryColorsPerButtonStylePerTheme[props.buttonStyle].idle.color};
  border: 1px solid ${secondaryColorsPerButtonStylePerTheme[props.buttonStyle].idle.borderColor};

  --icon-color: ${secondaryColorsPerButtonStylePerTheme[props.buttonStyle].idle.iconColor};

  ${hoverCapableMediaQuery} {
    &:hover {
      background-color: ${secondaryColorsPerButtonStylePerTheme[props.buttonStyle].hover.backgroundColor};
      color: ${secondaryColorsPerButtonStylePerTheme[props.buttonStyle].hover.color};
      border-color: ${secondaryColorsPerButtonStylePerTheme[props.buttonStyle].hover.borderColor};
      box-shadow: ${props.buttonStyle !== 'decorative' ? shadow.bottom['05'] : 0};

      --icon-color: ${secondaryColorsPerButtonStylePerTheme[props.buttonStyle].hover.iconColor};
    }
  }

  &:active,
  &:focus-visible {
    background-color: ${secondaryColorsPerButtonStylePerTheme[props.buttonStyle].active.backgroundColor};
    color: ${secondaryColorsPerButtonStylePerTheme[props.buttonStyle].active.color};
    border-color: ${secondaryColorsPerButtonStylePerTheme[props.buttonStyle].active.borderColor};
    box-shadow: ${props.buttonStyle !== 'decorative' ? shadow.bottom['05'] : 0};

    --icon-color: ${secondaryColorsPerButtonStylePerTheme[props.buttonStyle].active.iconColor};
  }

  &:disabled {
    background-color: ${secondaryColorsPerButtonStylePerTheme[props.buttonStyle].disabled.backgroundColor};
    color: ${secondaryColorsPerButtonStylePerTheme[props.buttonStyle].disabled.color};
    border-color: ${secondaryColorsPerButtonStylePerTheme[props.buttonStyle].disabled.borderColor};
    box-shadow: ${props.buttonStyle !== 'decorative' ? shadow.bottom['05'] : 0};

    --icon-color: ${secondaryColorsPerButtonStylePerTheme[props.buttonStyle].disabled.iconColor};
  }
`

const tertiaryStyle = (props: StyledButtonProps) => css`
  ${sizedStyle(props)}

  background-color: ${tertiaryColorsPerButtonStylePerTheme[props.buttonStyle].idle.backgroundColor};
  color: ${tertiaryColorsPerButtonStylePerTheme[props.buttonStyle].idle.color};

  --icon-color: ${tertiaryColorsPerButtonStylePerTheme[props.buttonStyle].idle.iconColor};

  ${hoverCapableMediaQuery} {
    &:hover {
      background-color: ${tertiaryColorsPerButtonStylePerTheme[props.buttonStyle].hover.backgroundColor};
      color: ${tertiaryColorsPerButtonStylePerTheme[props.buttonStyle].hover.color};

      --icon-color: ${tertiaryColorsPerButtonStylePerTheme[props.buttonStyle].hover.iconColor};
    }
  }

  &:active,
  &:focus-visible {
    background-color: ${tertiaryColorsPerButtonStylePerTheme[props.buttonStyle].active.backgroundColor};
    color: ${tertiaryColorsPerButtonStylePerTheme[props.buttonStyle].active.color};

    --icon-color: ${tertiaryColorsPerButtonStylePerTheme[props.buttonStyle].active.iconColor};
  }

  &:disabled {
    background-color: ${tertiaryColorsPerButtonStylePerTheme[props.buttonStyle].disabled.backgroundColor};
    color: ${tertiaryColorsPerButtonStylePerTheme[props.buttonStyle].disabled.color};

    --icon-color: ${tertiaryColorsPerButtonStylePerTheme[props.buttonStyle].disabled.iconColor};
  }
`

const textStyle = (props: StyledButtonProps) => css`
  gap: ${spacing[30]};

  padding: ${spacing[20]} 0;

  background-color: transparent;
  color: ${textColorsPerButtonStylePerTheme[props.buttonStyle].idle.color};

  --icon-color: ${textColorsPerButtonStylePerTheme[props.buttonStyle].idle.iconColor};

  ${hoverCapableMediaQuery} {
    &:hover {
      color: ${textColorsPerButtonStylePerTheme[props.buttonStyle].hover.color};

      --icon-color: ${textColorsPerButtonStylePerTheme[props.buttonStyle].hover.iconColor};
    }
  }

  &:active,
  &:focus-visible {
    color: ${textColorsPerButtonStylePerTheme[props.buttonStyle].active.color};

    --icon-color: ${textColorsPerButtonStylePerTheme[props.buttonStyle].active.iconColor};
  }

  &:disabled {
    color: ${textColorsPerButtonStylePerTheme[props.buttonStyle].disabled.color};

    --icon-color: ${textColorsPerButtonStylePerTheme[props.buttonStyle].disabled.iconColor};
  }

  text-decoration: underline;
`

const IconWrapper = styled.div`
  color: var(--icon-color);
  height: 100%;
  display: flex;
  align-items: center;
`

const buttonStylePerVariant: {
  [variant in ButtonVariant]: (props: StyledButtonProps) => SerializedStyles
} = {
  primary: primaryStyle,
  secondary: secondaryStyle,
  tertiary: tertiaryStyle,
  text: textStyle,
}

const primaryColorsPerButtonStylePerTheme: ButtonTheme = {
  base: {
    idle: {
      backgroundColor: colorTokens['color-bg-base-primary'],
      color: colorTokens['color-text-base-primary'],
      iconColor: colorTokens['color-fg-base-primary'],
    },
    hover: {
      backgroundColor: colorTokens['color-bg-base-primary-hover'],
      color: colorTokens['color-text-base-primary'],
      iconColor: colorTokens['color-fg-base-primary'],
    },
    active: {
      backgroundColor: colorTokens['color-bg-base-primary-active'],
      color: colorTokens['color-text-base-primary'],
      iconColor: colorTokens['color-fg-base-primary'],
    },
    disabled: {
      backgroundColor: colorTokens['color-bg-base-disable'],
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-base-disable'],
    },
  },
  danger: {
    idle: {
      backgroundColor: colorTokens['color-bg-danger-primary'],
      color: colorTokens['color-text-base-primary'],
      iconColor: colorTokens['color-fg-base-primary'],
    },
    hover: {
      backgroundColor: colorTokens['color-bg-danger-primary-hover'],
      color: colorTokens['color-text-base-primary'],
      iconColor: colorTokens['color-fg-base-primary'],
    },
    active: {
      backgroundColor: colorTokens['color-bg-danger-primary-active'],
      color: colorTokens['color-text-base-primary'],
      iconColor: colorTokens['color-fg-base-primary'],
    },
    disabled: {
      backgroundColor: colorTokens['color-bg-base-disable'],
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-base-disable'],
    },
  },
  success: {
    idle: {
      backgroundColor: colorTokens['color-bg-success-primary'],
      color: colorTokens['color-text-base-primary'],
      iconColor: colorTokens['color-fg-base-primary'],
    },
    hover: {
      backgroundColor: colorTokens['color-bg-success-primary-hover'],
      color: colorTokens['color-text-base-primary'],
      iconColor: colorTokens['color-fg-base-primary'],
    },
    active: {
      backgroundColor: colorTokens['color-bg-success-primary-active'],
      color: colorTokens['color-text-base-primary'],
      iconColor: colorTokens['color-fg-base-primary'],
    },
    disabled: {
      backgroundColor: colorTokens['color-bg-base-disable'],
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-base-disable'],
    },
  },
  warning: {
    idle: {
      backgroundColor: colorTokens['color-bg-warning-primary'],
      color: colorTokens['color-text-base-primary'],
      iconColor: colorTokens['color-fg-base-primary'],
    },
    hover: {
      backgroundColor: colorTokens['color-bg-warning-primary-hover'],
      color: colorTokens['color-text-base-primary'],
      iconColor: colorTokens['color-fg-base-primary'],
    },
    active: {
      backgroundColor: colorTokens['color-bg-warning-primary-active'],
      color: colorTokens['color-text-base-primary'],
      iconColor: colorTokens['color-fg-base-primary'],
    },
    disabled: {
      backgroundColor: colorTokens['color-bg-base-disable'],
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-base-disable'],
    },
  },
  decorative: {
    idle: {
      backgroundColor: colorTokens['color-bg-decorative-primary'],
      color: colorTokens['color-text-decorative-primary'],
      iconColor: colorTokens['color-fg-decorative-primary'],
    },
    hover: {
      backgroundColor: colorTokens['color-bg-decorative-primary-hover'],
      color: colorTokens['color-text-decorative-primary'],
      iconColor: colorTokens['color-fg-decorative-primary'],
    },
    active: {
      backgroundColor: colorTokens['color-bg-decorative-primary-active'],
      color: colorTokens['color-text-decorative-primary'],
      iconColor: colorTokens['color-fg-decorative-primary'],
    },
    disabled: {
      backgroundColor: colorTokens['color-bg-base-disable'],
      color: colorTokens['color-text-decorative-disable'],
      iconColor: colorTokens['color-fg-decorative-disable'],
    },
  },
  info: {
    idle: {
      backgroundColor: colorTokens['color-bg-info-primary'],
      color: colorTokens['color-text-base-primary'],
      iconColor: colorTokens['color-fg-base-primary'],
    },
    hover: {
      backgroundColor: colorTokens['color-bg-info-primary-hover'],
      color: colorTokens['color-text-base-primary'],
      iconColor: colorTokens['color-fg-base-primary'],
    },
    active: {
      backgroundColor: colorTokens['color-bg-info-primary-active'],
      color: colorTokens['color-text-base-primary'],
      iconColor: colorTokens['color-fg-base-primary'],
    },
    disabled: {
      backgroundColor: colorTokens['color-bg-base-disable'],
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-base-disable'],
    },
  },
}

const secondaryColorsPerButtonStylePerTheme: ButtonTheme = {
  base: {
    idle: {
      backgroundColor: colorTokens['color-bg-base-secondary'],
      color: colorTokens['color-text-base-secondary'],
      iconColor: colorTokens['color-fg-base'],
      borderColor: colorTokens['color-stroke-base-main'],
    },
    hover: {
      backgroundColor: colorTokens['color-bg-base-secondary-hover'],
      color: colorTokens['color-text-base-secondary'],
      iconColor: colorTokens['color-fg-base'],
      borderColor: colorTokens['color-stroke-base-main-hover'],
    },
    active: {
      backgroundColor: colorTokens['color-bg-base-secondary-active'],
      color: colorTokens['color-text-base-secondary'],
      iconColor: colorTokens['color-fg-base'],
      borderColor: colorTokens['color-stroke-base-main-active'],
    },
    disabled: {
      backgroundColor: colorTokens['color-bg-base-disable'],
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-base-disable'],
      borderColor: colorTokens['color-stroke-base-main-disable'],
    },
  },
  danger: {
    idle: {
      backgroundColor: colorTokens['color-bg-danger-secondary'],
      color: colorTokens['color-text-danger-secondary'],
      iconColor: colorTokens['color-fg-danger'],
      borderColor: colorTokens['color-stroke-danger'],
    },
    hover: {
      backgroundColor: colorTokens['color-bg-danger-secondary-hover'],
      color: colorTokens['color-text-danger-secondary'],
      iconColor: colorTokens['color-fg-danger'],
      borderColor: colorTokens['color-stroke-danger-hover'],
    },
    active: {
      backgroundColor: colorTokens['color-bg-danger-secondary-active'],
      color: colorTokens['color-text-danger-secondary'],
      iconColor: colorTokens['color-fg-danger'],
      borderColor: colorTokens['color-stroke-danger-active'],
    },
    disabled: {
      backgroundColor: colorTokens['color-bg-base-disable'],
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-base-disable'],
      borderColor: colorTokens['color-stroke-base-main-disable'],
    },
  },
  success: {
    idle: {
      backgroundColor: colorTokens['color-bg-success-secondary'],
      color: colorTokens['color-text-success-secondary'],
      iconColor: colorTokens['color-fg-success'],
      borderColor: colorTokens['color-stroke-success'],
    },
    hover: {
      backgroundColor: colorTokens['color-bg-success-secondary-hover'],
      color: colorTokens['color-text-success-secondary'],
      iconColor: colorTokens['color-fg-success'],
      borderColor: colorTokens['color-stroke-success-hover'],
    },
    active: {
      backgroundColor: colorTokens['color-bg-success-secondary-active'],
      color: colorTokens['color-text-success-secondary'],
      iconColor: colorTokens['color-fg-success'],
      borderColor: colorTokens['color-stroke-success-active'],
    },
    disabled: {
      backgroundColor: colorTokens['color-bg-base-disable'],
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-base-disable'],
      borderColor: colorTokens['color-stroke-base-main-disable'],
    },
  },
  warning: {
    idle: {
      backgroundColor: colorTokens['color-bg-warning-secondary'],
      color: colorTokens['color-text-warning-secondary'],
      iconColor: colorTokens['color-fg-warning'],
      borderColor: colorTokens['color-stroke-warning'],
    },
    hover: {
      backgroundColor: colorTokens['color-bg-warning-secondary-hover'],
      color: colorTokens['color-text-warning-secondary'],
      iconColor: colorTokens['color-fg-warning'],
      borderColor: colorTokens['color-stroke-warning-hover'],
    },
    active: {
      backgroundColor: colorTokens['color-bg-warning-secondary-active'],
      color: colorTokens['color-text-warning-secondary'],
      iconColor: colorTokens['color-fg-warning'],
      borderColor: colorTokens['color-stroke-warning-active'],
    },
    disabled: {
      backgroundColor: colorTokens['color-bg-base-disable'],
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-base-disable'],
      borderColor: colorTokens['color-stroke-base-main-disable'],
    },
  },
  decorative: {
    idle: {
      backgroundColor: colorTokens['color-bg-decorative-secondary'],
      color: colorTokens['color-text-decorative-secondary'],
      iconColor: colorTokens['color-fg-decorative-secondary'],
      borderColor: colorTokens['color-stroke-decorative'],
    },
    hover: {
      backgroundColor: colorTokens['color-bg-decorative-hover'],
      color: colorTokens['color-text-decorative-secondary'],
      iconColor: colorTokens['color-fg-decorative-secondary'],
      borderColor: colorTokens['color-stroke-decorative-hover'],
    },
    active: {
      backgroundColor: colorTokens['color-bg-decorative-active'],
      color: colorTokens['color-text-decorative-secondary'],
      iconColor: colorTokens['color-fg-decorative-secondary'],
      borderColor: colorTokens['color-stroke-decorative-active'],
    },
    disabled: {
      backgroundColor: colorTokens['color-bg-base-disable'],
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-decorative-disable'],
      borderColor: colorTokens['color-stroke-base-main-disable'],
    },
  },
  info: {
    idle: {
      backgroundColor: colorTokens['color-bg-info-secondary'],
      color: colorTokens['color-text-info-secondary'],
      iconColor: colorTokens['color-fg-info'],
      borderColor: colorTokens['color-stroke-info'],
    },
    hover: {
      backgroundColor: colorTokens['color-bg-info-secondary-hover'],
      color: colorTokens['color-text-info-secondary'],
      iconColor: colorTokens['color-fg-info'],
      borderColor: colorTokens['color-stroke-info-hover'],
    },
    active: {
      backgroundColor: colorTokens['color-bg-info-secondary-active'],
      color: colorTokens['color-text-info-secondary'],
      iconColor: colorTokens['color-fg-info'],
      borderColor: colorTokens['color-stroke-info-active'],
    },
    disabled: {
      backgroundColor: colorTokens['color-bg-base-disable'],
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-base-disable'],
      borderColor: colorTokens['color-stroke-base-main-disable'],
    },
  },
}

const tertiaryColorsPerButtonStylePerTheme: ButtonTheme = {
  base: {
    idle: {
      backgroundColor: colorTokens['color-bg-base-tertiary'],
      color: colorTokens['color-text-base-tertiary'],
      iconColor: colorTokens['color-fg-base-tertiary'],
    },
    hover: {
      backgroundColor: colorTokens['color-bg-base-tertiary-hover'],
      color: colorTokens['color-text-base-tertiary'],
      iconColor: colorTokens['color-fg-base-tertiary'],
    },
    active: {
      backgroundColor: colorTokens['color-bg-base-tertiary-active'],
      color: colorTokens['color-text-base-tertiary'],
      iconColor: colorTokens['color-fg-base-tertiary'],
    },
    disabled: {
      backgroundColor: 'transparent',
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-base-disable'],
    },
  },
  danger: {
    idle: {
      backgroundColor: colorTokens['color-bg-danger-tertiary'],
      color: colorTokens['color-text-danger-secondary'],
      iconColor: colorTokens['color-fg-danger'],
    },
    hover: {
      backgroundColor: colorTokens['color-bg-danger-tertiary-hover'],
      color: colorTokens['color-text-danger-secondary'],
      iconColor: colorTokens['color-fg-danger'],
    },
    active: {
      backgroundColor: colorTokens['color-bg-danger-tertiary-active'],
      color: colorTokens['color-text-danger-secondary'],
      iconColor: colorTokens['color-fg-danger'],
    },
    disabled: {
      backgroundColor: 'transparent',
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-base-disable'],
    },
  },
  success: {
    idle: {
      backgroundColor: colorTokens['color-bg-success-tertiary'],
      color: colorTokens['color-text-success-secondary'],
      iconColor: colorTokens['color-fg-success'],
    },
    hover: {
      backgroundColor: colorTokens['color-bg-success-tertiary-hover'],
      color: colorTokens['color-text-success-secondary'],
      iconColor: colorTokens['color-fg-success'],
    },
    active: {
      backgroundColor: colorTokens['color-bg-success-tertiary-active'],
      color: colorTokens['color-text-success-secondary'],
      iconColor: colorTokens['color-fg-success'],
    },
    disabled: {
      backgroundColor: 'transparent',
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-base-disable'],
    },
  },
  warning: {
    idle: {
      backgroundColor: colorTokens['color-bg-warning-tertiary'],
      color: colorTokens['color-text-warning-secondary'],
      iconColor: colorTokens['color-fg-warning'],
    },
    hover: {
      backgroundColor: colorTokens['color-bg-warning-tertiary-hover'],
      color: colorTokens['color-text-warning-secondary'],
      iconColor: colorTokens['color-fg-warning'],
    },
    active: {
      backgroundColor: colorTokens['color-bg-warning-tertiary-active'],
      color: colorTokens['color-text-warning-secondary'],
      iconColor: colorTokens['color-fg-warning'],
    },
    disabled: {
      backgroundColor: 'transparent',
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-base-disable'],
    },
  },
  decorative: {
    idle: {
      backgroundColor: colorTokens['color-bg-decorative-tertiary'],
      color: colorTokens['color-text-decorative-tertiary'],
      iconColor: colorTokens['color-fg-decorative-tertiary'],
    },
    hover: {
      backgroundColor: colorTokens['color-bg-decorative-hover'],
      color: colorTokens['color-text-decorative-tertiary'],
      iconColor: colorTokens['color-fg-decorative-tertiary'],
    },
    active: {
      backgroundColor: colorTokens['color-bg-decorative-hover'],
      color: colorTokens['color-text-decorative-tertiary'],
      iconColor: colorTokens['color-fg-decorative-tertiary'],
    },
    disabled: {
      backgroundColor: 'transparent',
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-decorative-disable'],
    },
  },
  info: {
    idle: {
      backgroundColor: colorTokens['color-bg-info-tertiary'],
      color: colorTokens['color-text-info-secondary'],
      iconColor: colorTokens['color-fg-info'],
    },
    hover: {
      backgroundColor: colorTokens['color-bg-info-tertiary-hover'],
      color: colorTokens['color-text-info-secondary'],
      iconColor: colorTokens['color-fg-info'],
    },
    active: {
      backgroundColor: colorTokens['color-bg-info-tertiary-active'],
      color: colorTokens['color-text-info-secondary'],
      iconColor: colorTokens['color-fg-info'],
    },
    disabled: {
      backgroundColor: 'transparent',
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-base-disable'],
    },
  },
}

const textColorsPerButtonStylePerTheme = {
  base: {
    idle: {
      color: colorTokens['color-text-base-secondary'],
      iconColor: colorTokens['color-fg-base'],
    },
    hover: {
      color: colorTokens['color-text-base-secondary'],
      iconColor: colorTokens['color-fg-base'],
    },
    active: {
      color: colorTokens['color-text-base-secondary'],
      iconColor: colorTokens['color-fg-base'],
    },
    disabled: {
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-base-disable'],
    },
  },
  danger: {
    idle: {
      color: colorTokens['color-text-danger-secondary'],
      iconColor: colorTokens['color-fg-danger'],
    },
    hover: {
      color: colorTokens['color-text-danger-secondary'],
      iconColor: colorTokens['color-fg-danger'],
    },
    active: {
      color: colorTokens['color-text-danger-secondary'],
      iconColor: colorTokens['color-fg-danger'],
    },
    disabled: {
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-base-disable'],
    },
  },
  success: {
    idle: {
      color: colorTokens['color-text-success-secondary'],
      iconColor: colorTokens['color-fg-success'],
    },
    hover: {
      color: colorTokens['color-text-success-secondary'],
      iconColor: colorTokens['color-fg-success'],
    },
    active: {
      color: colorTokens['color-text-success-secondary'],
      iconColor: colorTokens['color-fg-success'],
    },
    disabled: {
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-base-disable'],
    },
  },
  warning: {
    idle: {
      color: colorTokens['color-text-warning-secondary'],
      iconColor: colorTokens['color-fg-warning'],
    },
    hover: {
      color: colorTokens['color-text-warning-secondary'],
      iconColor: colorTokens['color-fg-warning'],
    },
    active: {
      color: colorTokens['color-text-warning-secondary'],
      iconColor: colorTokens['color-fg-warning'],
    },
    disabled: {
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-base-disable'],
    },
  },
  decorative: {
    idle: {
      color: colorTokens['color-text-decorative-secondary'],
      iconColor: colorTokens['color-fg-decorative-secondary'],
    },
    hover: {
      color: colorTokens['color-text-decorative-secondary'],
      iconColor: colorTokens['color-fg-decorative-secondary'],
    },
    active: {
      color: colorTokens['color-text-decorative-secondary'],
      iconColor: colorTokens['color-fg-decorative-secondary'],
    },
    disabled: {
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-decorative-disable'],
    },
  },
  info: {
    idle: {
      color: colorTokens['color-text-info-secondary'],
      iconColor: colorTokens['color-fg-info'],
    },
    hover: {
      color: colorTokens['color-text-info-secondary'],
      iconColor: colorTokens['color-fg-info'],
    },
    active: {
      color: colorTokens['color-text-info-secondary'],
      iconColor: colorTokens['color-fg-info'],
    },
    disabled: {
      color: colorTokens['color-text-base-disable'],
      iconColor: colorTokens['color-fg-base-disable'],
    },
  },
}

const buttonThemePerVariant: {
  [variant in 'primary' | 'secondary' | 'tertiary']: ButtonTheme
} = {
  primary: primaryColorsPerButtonStylePerTheme,
  secondary: secondaryColorsPerButtonStylePerTheme,
  tertiary: tertiaryColorsPerButtonStylePerTheme,
}

const getSpinnerColor = (disabled: boolean, variant: ButtonVariant, style: ButtonStyle): string => {
  if (disabled) {
    return colorTokens['color-fg-base-disable']
  }

  if (variant === 'text') {
    return colorTokens['color-text-base-secondary']
  }

  return buttonThemePerVariant[variant][style].idle.color
}
